import React from 'react';
import { Grid } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ReferenceLine
} from 'recharts';
import { formatDate } from '../../utils/dateFormatters';
import { filterByNits } from '../../utils/inventoryReports';

const { useBreakpoint } = Grid;

/**
 * VelocityOfChangeChart - Ritmo de Mejora del Inventario (Responsive)
 * Muestra si el inventario esta mejorando o empeorando dia a dia.
 * Valores positivos = mejorando, valores negativos = empeorando.
 * @param {Object} props
 * @param {Array} props.data - Array of DailyInventoryDistributionRecord
 * @param {string[]} props.nitList - Selected NITs for filtering
 */
function VelocityOfChangeChart({ data, nitList }) {
  const screens = useBreakpoint();
  const isXs = !screens.xs;
  const isSm = screens.sm;
  const isMd = screens.md;

  // Transform data and calculate velocity of change
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    const relevantData = filterByNits(data, nitList);

    // Aggregate by date if grouped by NIT
    const groupedByDate = {};
    relevantData.forEach(record => {
      const date = record.RecordDate;
      if (!groupedByDate[date]) {
        groupedByDate[date] = {
          RecordDate: date,
          VerdeCount: 0,
          AmarilloCount: 0,
          TotalReferences: 0
        };
      }
      groupedByDate[date].VerdeCount += record.VerdeCount || 0;
      groupedByDate[date].AmarilloCount += record.AmarilloCount || 0;
      groupedByDate[date].TotalReferences += record.TotalReferences || 0;
    });

    const sortedData = Object.values(groupedByDate)
      .sort((a, b) => a.RecordDate.localeCompare(b.RecordDate));

    // Calculate velocity of change (day over day change)
    const withVelocity = sortedData.map((item, index) => {
      const healthyCount = item.VerdeCount + item.AmarilloCount;
      const healthPercent = item.TotalReferences > 0
        ? (healthyCount / item.TotalReferences) * 100
        : 0;

      let velocity = 0;
      if (index > 0) {
        const prevHealthy = sortedData[index - 1].VerdeCount + sortedData[index - 1].AmarilloCount;
        const prevHealthPercent = sortedData[index - 1].TotalReferences > 0
          ? (prevHealthy / sortedData[index - 1].TotalReferences) * 100
          : 0;
        velocity = healthPercent - prevHealthPercent;
      }

      return {
        RecordDate: item.RecordDate,
        HealthPercent: healthPercent,
        Velocity: velocity,
        VerdeCount: item.VerdeCount,
        AmarilloCount: item.AmarilloCount
      };
    });

    return withVelocity;
  }, [data, nitList]);

  // Responsive settings
  const chartHeight = isXs ? 200 : isSm ? 280 : 350;
  const marginValue = isXs ? 8 : 20;
  const dotSize = isXs ? 2 : 4;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '8px',
        fontSize: '12px'
      }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>{formatDate(label)}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: '2px 0', color: entry.color }}>
            {entry.name}: {entry.name.includes('Ritmo')
              ? `${entry.value > 0 ? '+' : ''}${entry.value.toFixed(1)}% ${entry.value > 0 ? '(mejorando)' : entry.value < 0 ? '(empeorando)' : '(sin cambio)'}`
              : `${entry.value.toFixed(1)}%`
            }
          </p>
        ))}
      </div>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: chartHeight,
        color: '#999',
        fontSize: isXs ? 12 : 14
      }}>
        No hay datos disponibles
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <LineChart
        data={chartData}
        margin={{ top: marginValue, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="RecordDate"
          tickFormatter={formatDate}
          angle={isXs ? -90 : -45}
          textAnchor="end"
          height={isXs ? 50 : 80}
          style={{ fontSize: isXs ? 9 : 11 }}
          interval={isXs ? 1 : 0}
        />
        <YAxis tickFormatter={(v) => `${v}%`} style={{ fontSize: isXs ? 9 : 11 }} width={isXs ? 35 : 40} />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: isXs ? 10 : 12 }}
          iconType={isXs ? 'circle' : 'rect'}
        />
        <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" label={{ value: 'Sin cambio', position: 'insideTopRight', style: { fontSize: '10px', fill: '#9ca3af' } }} />
        <Line
          type="monotone"
          dataKey="HealthPercent"
          name={isXs ? 'Salud' : 'Salud del Inventario'}
          stroke="#52c41a"
          strokeWidth={2}
          dot={{ r: dotSize }}
        />
        <Line
          type="monotone"
          dataKey="Velocity"
          name={isXs ? 'Ritmo' : 'Ritmo de Cambio'}
          stroke="#1890ff"
          strokeWidth={2}
          dot={{ r: dotSize }}
          strokeDasharray="5 5"
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default VelocityOfChangeChart;
